import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Heart, CheckCircle2, Star, MapPin, Compass } from "lucide-react";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import credentialsImg from "@assets/image_1775160323162.png";

export default function Home() {
  return (
    <div className="flex flex-col gap-0 w-full overflow-hidden">
      <SEO
        title="Premier Dog Walking and Pet Sitting in Tallahassee"
        description="Tallahassee Lassie is your local, woman-owned pet care service. Award-winning dog walking, pet sitting, and VIP programs with a 100% reliability guarantee. No breed restrictions. Serving all of Leon County."
        path="/"
      />
      <section className="relative w-full min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/16617bc9-4b44-4480-8f95-5470f189f37e/IMG_4166_jpg.jpg?format=1500w"
            alt=""
            fetchPriority="high"
            className="w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/40"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/80"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-1 text-yellow-500">
                {[...Array(5)].map((_, i) => <Star key={i} fill="currentColor" size={16} />)}
              </div>
              <span className="text-sm font-medium text-muted-foreground">Tallahassee's top-rated pet care</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground leading-[1.08] tracking-tight mb-6">
              You're Busy;<br />
              <span className="text-primary">We will Take the Leash</span>
            </h1>

            <p className="text-xl text-muted-foreground max-w-lg leading-relaxed mb-8">
              Do you need a reliable and consistent midday dog walker or pet sitter? Recurring schedule you can set and forget? On-Demand Add-Ons? Morning, evening, and weekend dog walking available, too!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button asChild size="lg" className="w-full sm:w-auto text-lg h-14 px-8 shadow-lg shadow-primary/20 transition-all hover:translate-y-[-2px]">
                <Link href="/contact-us">
                  Request a Meet & Greet
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto text-lg h-14 px-8 border-2">
                <Link href="/menu">
                  View Our Services
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-600" />
                100% Reliability Guarantee
              </span>
              <span className="flex items-center gap-2">
                <Heart size={16} className="text-primary" fill="currentColor" />
                No Breed Restrictions
              </span>
              <span className="flex items-center gap-2">
                <MapPin size={16} className="text-secondary" />
                Woman-Owned & Local
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">Why Choose Tallahassee Lassie?</h2>
              <p className="text-lg text-muted-foreground">
                Tallahassee Lassie is your local, woman-owned pet service. All members of our team are true animal lovers who receive formal training and orientation. Our mission is to provide Tallahassee Pet Parents with knowledgeable, reliable, and exceptional care.
              </p>
            </div>
          </Reveal>
          
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <p className="font-serif text-xl font-bold text-foreground mb-4">All clients receive the following perks:</p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Trustworthy and reliable care",
                "Professional training with continuing education",
                "Locally founded, owned, and operated",
                "100% Reliability Guarantee",
                "Recurring scheduling",
                "Automatic Billing",
                "Updates with each visit",
                "Peace of mind"
              ].map((perk, i) => (
                <Reveal key={i} delay={i * 50}>
                  <div className="flex items-center gap-3 p-4 bg-muted/30 hover:bg-white transition-all" style={{ borderRadius: "16px 0 16px 0" }}>
                    <CheckCircle2 className="text-primary flex-shrink-0" size={20} />
                    <span className="font-medium text-foreground/80">{perk}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <Reveal>
            <div
              className="max-w-5xl mx-auto bg-primary text-white p-8 md:p-12 relative overflow-hidden"
              style={{ borderRadius: "32px 0 32px 0" }}
            >
              <div className="absolute inset-0 bg-[url('https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/b885628e-de6b-49d7-bf64-f592f9783add/DogWalker.jpg')] bg-cover bg-center opacity-[0.10] mix-blend-overlay"></div>
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8 text-center lg:text-left">
                <span className="w-20 h-20 shrink-0 bg-white/15 text-white flex items-center justify-center" style={{ borderRadius: "20px 0 20px 0" }}>
                  <Compass size={40} />
                </span>
                <div className="flex-1">
                  <p className="uppercase tracking-[0.2em] text-sm text-white/70 mb-2 font-medium">
                    Not sure what you need?
                  </p>
                  <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3">
                    Find Your Pet's Perfect Care Plan
                  </h2>
                  <p className="text-lg text-white/90 max-w-xl">
                    Answer five quick questions and we'll match you with the right service and a price range — then book a free call. Takes about a minute.
                  </p>
                </div>
                <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 h-14 px-8 text-lg whitespace-nowrap">
                  <Link href="/care-plan-finder">
                    Find My Plan &rarr;
                  </Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal direction="left" className="order-2 lg:order-1 relative">
              <img 
                src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/b885628e-de6b-49d7-bf64-f592f9783add/DogWalker.jpg?format=750w" 
                alt="Dog walker with a happy dog in Tallahassee" 
                width={512}
                height={384}
                loading="lazy"
                className="w-full h-auto object-cover shadow-lg"
                style={{ borderRadius: "0 40px 0 40px" }}
              />
            </Reveal>
            <Reveal direction="right" className="order-1 lg:order-2 space-y-6">
              <h2 className="text-4xl font-serif font-bold text-foreground">Giving Back to Tallahassee</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Proceeds from our business benefit local nonprofit agencies and area animal rescues. We are currently excited about partnering with Independence Landing, providing support to residents; as well as our new Lend-a-Leash program for the Tallahassee Community.
              </p>
              <p className="text-xl font-serif font-bold text-primary italic">
                Thank you for supporting these efforts.
              </p>
              <div className="pt-2">
                <Button asChild size="lg" variant="outline" className="text-lg h-14 px-8 border-2">
                  <Link href="/giving">
                    Learn About Our Giving &rarr;
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white overflow-hidden relative">
        <div className="container mx-auto px-4 relative z-10">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="text-4xl font-serif font-bold text-foreground mb-4">Worth Barkin About!</h2>
              <div className="flex justify-center gap-1 mb-6 text-yellow-400">
                {[...Array(5)].map((_, i) => <Star key={i} fill="currentColor" size={24} />)}
              </div>
              <p className="text-lg text-muted-foreground">Don't just take our word for it. Hear from our amazing clients across Tallahassee.</p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Reveal delay={0}>
              <div className="bg-primary/5 p-8 relative card-lift h-full" style={{ borderRadius: "24px 0 24px 0" }}>
                <Heart className="absolute top-6 right-6 text-primary/20" size={48} fill="currentColor" />
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/ca936253-f0f0-46f7-a23f-80fbb38bc677/George.jpg?format=300w" 
                    alt="George the spaniel" 
                    width={64}
                    height={64}
                    loading="lazy"
                    className="w-16 h-16 rounded-full object-cover border-2 border-primary"
                  />
                  <div>
                    <h3 className="font-bold text-lg font-serif">Anna D. & George</h3>
                    <p className="text-sm text-muted-foreground">VIP Dog Walking</p>
                  </div>
                </div>
                <p className="text-lg italic text-foreground/80 leading-relaxed">
                  "Tallahassee Lassie has been wonderful! They take George, our energetic springer spaniel, for long walks twice a week and he loves it! They have been so easy to work with and very accommodating. I love the walk updates while I'm at work!"
                </p>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div className="bg-primary/5 p-8 relative card-lift h-full" style={{ borderRadius: "24px 0 24px 0" }}>
                <Heart className="absolute top-6 right-6 text-secondary/20" size={48} fill="currentColor" />
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/1704215622943-1KXSQCE1QNTBWBVRME5Y/image-asset.jpeg?format=300w" 
                    alt="Jude the beagle" 
                    width={64}
                    height={64}
                    loading="lazy"
                    className="w-16 h-16 rounded-full object-cover border-2 border-secondary"
                  />
                  <div>
                    <h3 className="font-bold text-lg font-serif">Taylor F. & Jude</h3>
                    <p className="text-sm text-muted-foreground">VIP Dog Walking</p>
                  </div>
                </div>
                <p className="text-lg italic text-foreground/80 leading-relaxed">
                  "I've had the pleasure of working with Tally Lassie for a while now and I couldn't be happier! I'm never worried about leaving my fur baby home while I'm working because I know that she'll be well cared for by their staff. Everyone is extremely kind, detailed, and accommodating to my crazy schedule and the needs of my pup. The communication is never lacking!!"
                </p>
              </div>
            </Reveal>
          </div>
          
          <Reveal>
            <div className="text-center mt-12">
              <Button asChild variant="ghost" className="font-medium text-primary hover:text-primary/80">
                <Link href="/reviews">
                  Read More Reviews &rarr;
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-8">
              <h2 className="text-3xl font-serif font-bold text-foreground mb-3">Award-Winning & Certified</h2>
              <p className="text-muted-foreground">
                Tallahassee Lassie was named the Best in Florida and Best of Next Door 2020, 2021, 2022; and we are proud to be certified by the following entities.
              </p>
            </div>
          </Reveal>
          <Reveal>
            <div className="max-w-2xl mx-auto">
              <img
                src={credentialsImg}
                alt="Neighborhood Favorite Nextdoor 2020-2022, Voted Best of Florida 2022, PSI, NAPPS, ProPetHero, and FetchFind certified"
                width={991}
                height={613}
                loading="lazy"
                className="w-full"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-white">
        <Reveal className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-6">We Can't Wait to Meet You!</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-muted-foreground">
            After our Meet & Greet, you'll be assigned a primary sitter, a secondary backup, and an emergency back-up, meaning your services are always covered!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="text-lg h-14 px-8" style={{ borderRadius: "16px 0 16px 0" }}>
              <Link href="/contact-us">
                Request a Meet & Greet
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-secondary text-white hover:bg-secondary/90 text-lg h-14 px-8 border-none" style={{ borderRadius: "16px 0 16px 0" }}>
              <Link href="/vip-program">
                Explore VIP Program
              </Link>
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
